// Security utilities for input validation and sanitization

import DOMPurify from 'dompurify';

/**
 * Sanitizes HTML input to prevent XSS attacks
 */
export const sanitizeHtml = (input: string): string => {
  return DOMPurify.sanitize(input, {
    ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'p', 'br'],
    ALLOWED_ATTR: []
  });
};

/**
 * Validates and sanitizes text input
 */
export const sanitizeText = (input: string, maxLength: number = 500): string => {
  if (typeof input !== 'string') {
    throw new Error('Input must be a string');
  }
  
  // Remove any HTML tags and trim
  const cleaned = input.replace(/<[^>]*>/g, '').trim();
  
  // Additional security: prevent common injection patterns
  const dangerousPatterns = /[<>'"`;\\]/g;
  const sanitized = cleaned.replace(dangerousPatterns, '');
  
  // Enforce length limits
  if (sanitized.length > maxLength) {
    throw new Error(`Input exceeds maximum length of ${maxLength} characters`);
  }
  
  return sanitized;
};

/**
 * Validates and sanitizes address input
 */
export const sanitizeAddress = (address: string): string => {
  if (typeof address !== 'string') {
    throw new Error('Address must be a string');
  }
  
  // Remove HTML tags but allow basic punctuation for addresses
  const cleaned = address.replace(/<[^>]*>/g, '').trim();
  
  // More permissive pattern for UK addresses - allow letters, numbers, spaces, and common punctuation
  const addressPattern = /^[a-zA-Z0-9\s,.\-#/'()&]+$/;
  
  if (!addressPattern.test(cleaned)) {
    console.error('Address validation failed for:', cleaned);
    console.error('Characters causing issues:', cleaned.match(/[^a-zA-Z0-9\s,.\-#/'()&]/g));
    throw new Error('Address contains invalid characters. Only letters, numbers, spaces, and common punctuation allowed.');
  }
  
  if (cleaned.length > 200) {
    throw new Error('Address exceeds maximum length of 200 characters');
  }
  
  return cleaned;
};

/**
 * Validates email format
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // simpler allow-list to avoid false negatives
  return emailRegex.test(email);
};

/**
 * Validates phone number format (enhanced validation)
 * Enhanced to prevent common injection patterns
 */
export const validatePhone = (phone: string): boolean => {
  if (typeof phone !== 'string' || phone.length > 20) {
    return false;
  }
  
  // Remove allowed formatting characters
  const cleaned = phone.replace(/[\s\-\(\)]/g, '');
  
  // UK phone number format: allow numbers starting with 0 (like 07123456789 or 01234567890)
  // Also allow international format with + prefix
  const phoneRegex = /^(\+?[1-9]\d{7,15}|0\d{10})$/;
  
  // Additional security: prevent SQL injection and XSS patterns
  const dangerousPatterns = /['"`;\\<>]/;
  
  return phoneRegex.test(cleaned) && !dangerousPatterns.test(phone);
};

// Enhanced email masking for security logs
export const maskEmail = (email: string): string => {
  if (!email || !email.includes('@')) return '***';
  
  const [username, domain] = email.split('@');
  if (username.length <= 3) return '***@' + domain;
  
  return username.substring(0, 3) + '***@' + domain;
};

// Enhanced phone masking for security logs
export const maskPhone = (phone: string): string => {
  if (!phone || phone.length <= 6) return '***';
  
  return phone.substring(0, 3) + '***' + phone.substring(phone.length - 2);
};

/**
 * Validates role values
 */
export const validateRole = (role: string): role is 'student' | 'instructor' => {
  return role === 'student' || role === 'instructor';
};

/**
 * Validates theory test status
 */
export const validateTheoryTestStatus = (status: string): status is 'not_started' | 'booked' | 'passed' => {
  return status === 'not_started' || status === 'booked' || status === 'passed';
};

/**
 * Rate limiting for authentication attempts
 */
class RateLimiter {
  private attempts: Map<string, number[]> = new Map();
  private maxAttempts: number;
  private windowMs: number;

  constructor(maxAttempts = 5, windowMs = 15 * 60 * 1000) { // 5 attempts per 15 minutes
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
  }

  isAllowed(identifier: string): boolean {
    const now = Date.now();
    const attempts = this.attempts.get(identifier) || [];
    
    // Filter out old attempts
    const recentAttempts = attempts.filter(time => now - time < this.windowMs);
    
    if (recentAttempts.length >= this.maxAttempts) {
      return false;
    }

    // Record this attempt
    recentAttempts.push(now);
    this.attempts.set(identifier, recentAttempts);
    
    return true;
  }

  reset(identifier: string): void {
    this.attempts.delete(identifier);
  }
}

export const authRateLimiter = new RateLimiter();

/**
 * Secure session timeout handling
 */
export const SESSION_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour

export const checkSessionTimeout = (lastActivity: number): boolean => {
  return Date.now() - lastActivity > SESSION_TIMEOUT_MS;
};

/**
 * Content Security Policy helper - Production-ready CSP
 */
export const getCSPHeader = (isDevelopment: boolean = false): string => {
  const scriptSrc = isDevelopment 
    ? "script-src 'self' 'unsafe-inline' 'unsafe-eval'" // Development only
    : "script-src 'self'"; // Production: no unsafe directives
    
  return [
    "default-src 'self'",
    scriptSrc,
    "style-src 'self' 'unsafe-inline'", // Keep for CSS-in-JS libraries
    "img-src 'self' data: https:",
    "font-src 'self' https: data:",
    "connect-src 'self' https://bkkxfrbbvchrxcdylyut.supabase.co wss://bkkxfrbbvchrxcdylyut.supabase.co",
    "frame-ancestors 'none'",
    "form-action 'self'",
    "base-uri 'self'",
    "object-src 'none'",
    "upgrade-insecure-requests",
    "require-trusted-types-for 'script'" // Additional XSS protection
  ].join('; ');
};

/**
 * Additional security headers for production
 */
export const getSecurityHeaders = (): Record<string, string> => {
  return {
    'Content-Security-Policy': getCSPHeader(),
    'X-Content-Type-Options': 'nosniff',
    'X-Frame-Options': 'DENY',
    'X-XSS-Protection': '1; mode=block',
    'Referrer-Policy': 'strict-origin-when-cross-origin',
    'Strict-Transport-Security': 'max-age=31536000; includeSubDomains',
    'Permissions-Policy': 'camera=(), microphone=(), geolocation=()',
    'Cross-Origin-Opener-Policy': 'same-origin',
    'Cross-Origin-Embedder-Policy': 'require-corp'
  };
};

/**
 * Security monitoring utilities
 */
export const querySecurityAuditLogs = (timeframe: 'last_hour' | 'last_day' | 'last_week' = 'last_day') => {
  const intervals = {
    last_hour: "1 hour",
    last_day: "1 day", 
    last_week: "7 days"
  };
  
  return `
    SELECT 
      created_at,
      action_type,
      table_name,
      user_id,
      ip_address,
      old_values,
      new_values
    FROM security_audit_log 
    WHERE created_at >= NOW() - INTERVAL '${intervals[timeframe]}'
    ORDER BY created_at DESC
    LIMIT 100
  `;
};

/**
 * Query for authentication failures
 */
export const queryAuthFailures = (timeframe: 'last_hour' | 'last_day' | 'last_week' = 'last_day') => {
  const intervals = {
    last_hour: "1 hour",
    last_day: "1 day",
    last_week: "7 days"
  };
  
  return `
    SELECT 
      created_at,
      old_values->>'email' as attempted_email,
      old_values->>'reason' as failure_reason,
      ip_address,
      user_agent
    FROM security_audit_log 
    WHERE action_type = 'auth_failure'
      AND created_at >= NOW() - INTERVAL '${intervals[timeframe]}'
    ORDER BY created_at DESC
    LIMIT 50
  `;
};

/**
 * Enhanced PII field validation and masking
 */
export const validateLicenceNumber = (licence: string): boolean => {
  if (typeof licence !== 'string' || licence.length === 0) {
    return false;
  }
  
  // UK driving licence format validation
  const ukLicencePattern = /^[A-Z9]{5}\d{6}[A-Z9]{2}\d[A-Z]{2}$/;
  const cleaned = licence.replace(/\s/g, '').toUpperCase();
  
  // Additional security: prevent injection patterns
  const dangerousPatterns = /['"`;\\<>]/;
  
  return ukLicencePattern.test(cleaned) && !dangerousPatterns.test(licence);
};

/**
 * Mask sensitive data for logging (PII protection)
 */
export const maskSensitiveData = (value: string | null, type: 'email' | 'phone' | 'licence' | 'address' = 'email'): string => {
  if (!value || value.length === 0) return '***';
  
  switch (type) {
    case 'email':
      const emailParts = value.split('@');
      if (emailParts.length !== 2) return '***@***.***';
      const [localPart, domain] = emailParts;
      const maskedLocal = localPart.length > 2 ? localPart.substring(0, 2) + '***' : '***';
      const maskedDomain = domain.length > 4 ? '***.' + domain.substring(domain.lastIndexOf('.')) : '***.***';
      return maskedLocal + '@' + maskedDomain;
      
    case 'phone':
      if (value.length <= 6) return '***';
      return value.substring(0, 3) + '***' + value.substring(value.length - 2);
      
    case 'licence':
      if (value.length <= 8) return '***';
      return value.substring(0, 4) + '***' + value.substring(value.length - 2);
      
    case 'address':
      if (value.length <= 10) return '*** *** ***';
      const words = value.split(' ');
      if (words.length === 1) return words[0].substring(0, 3) + '***';
      return words[0] + ' *** ' + words[words.length - 1];
      
    default:
      return value.length > 5 ? value.substring(0, 3) + '***' + value.substring(value.length - 2) : '***';
  }
};

/**
 * Enhanced session security with device fingerprinting simulation
 */
export const generateSessionFingerprint = (): string => {
  try {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.textBaseline = 'top';
      ctx.font = '14px Arial';
      ctx.fillText('Session fingerprint', 2, 2);
    }
    
    const fingerprint = [
      navigator.userAgent,
      navigator.language,
      screen.width + 'x' + screen.height,
      new Date().getTimezoneOffset(),
      !!window.sessionStorage,
      !!window.localStorage,
      canvas.toDataURL()
    ].join('|');
    
    // Simple hash function for fingerprint
    let hash = 0;
    for (let i = 0; i < fingerprint.length; i++) {
      const char = fingerprint.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    return Math.abs(hash).toString(36);
  } catch (error) {
    console.error('Error generating session fingerprint:', error);
    // Return a fallback fingerprint
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }
};

/**
 * Validate session integrity
 */
export const validateSessionIntegrity = (storedFingerprint: string): boolean => {
  try {
    const currentFingerprint = generateSessionFingerprint();
    return storedFingerprint === currentFingerprint;
  } catch (error) {
    console.error('Error validating session integrity:', error);
    return true; // Allow access on validation error
  }
};

/**
 * Enhanced CSRF protection for critical operations
 */
export const generateCSRFToken = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Validate CSRF token
 */
export const validateCSRFToken = (token: string, storedToken: string): boolean => {
  if (!token || !storedToken || token.length !== storedToken.length) {
    return false;
  }
  
  // Constant-time comparison to prevent timing attacks
  let result = 0;
  for (let i = 0; i < token.length; i++) {
    result |= token.charCodeAt(i) ^ storedToken.charCodeAt(i);
  }
  
  return result === 0;
};

/**
 * Security monitoring for suspicious patterns
 */
export const detectSuspiciousActivity = (activities: Array<{
  timestamp: number;
  action: string;
  ip?: string;
  userAgent?: string;
}>): Array<{
  type: string;
  severity: 'low' | 'medium' | 'high';
  description: string;
}> => {
  const alerts: Array<{
    type: string;
    severity: 'low' | 'medium' | 'high';
    description: string;
  }> = [];
  
  const now = Date.now();
  const recentActivities = activities.filter(a => now - a.timestamp < 60 * 60 * 1000); // Last hour
  
  // Check for rapid successive attempts
  if (recentActivities.length > 20) {
    alerts.push({
      type: 'rapid_requests',
      severity: 'high',
      description: 'Unusually high number of requests in the last hour'
    });
  }
  
  // Check for multiple IP addresses
  const uniqueIPs = new Set(recentActivities.map(a => a.ip).filter(Boolean));
  if (uniqueIPs.size > 3) {
    alerts.push({
      type: 'multiple_ips',
      severity: 'medium',
      description: 'Multiple IP addresses detected for same user session'
    });
  }
  
  // Check for different user agents
  const uniqueUserAgents = new Set(recentActivities.map(a => a.userAgent).filter(Boolean));
  if (uniqueUserAgents.size > 2) {
    alerts.push({
      type: 'multiple_user_agents',
      severity: 'medium',
      description: 'Multiple user agents detected for same user session'
    });
  }
  
  return alerts;
};